import type {
  UpdatePersonResponse,
  LoadPeopleResponse,
  Person,
  Household,
  LoadPersonResponse,
} from '../types';
import type { PersonId } from '@/shared/types/profile';
import type { PeopleStore } from '..';
import { apiInvoke, shouldLoad } from '../../helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import { Paths } from '../../constants';
import type { APIParams, APILoadParams } from '@/store/helpers/types';

interface BaseLoadPeopleParams {
  count?: boolean;
  search?: LoadPeopleSearchParams;
  criteria?: {
    campus_id?: string | string[];
    created?: {
      operator: 'on';
      value: string;
    };
    gender?: 'm' | 'f';
    member_status?: string[];
    groups?: {
      laygroup1?: string;
      laygroup2?: string;
    };
    tags?: string[];
  };
  order?: {
    field: keyof Person['data'];
    desc: boolean;
  }[];
  limit?: number;
  offset?: number;
  timezone?: string;
}

interface CachedLoadPeopleParams extends BaseLoadPeopleParams {
  cache: true;
}

interface UncachedLoadPeopleParams extends BaseLoadPeopleParams {
  cache?: false;
}

export type LoadPeopleParams =
  | CachedLoadPeopleParams
  | UncachedLoadPeopleParams;

export interface LoadPeopleSearchParams {
  type: 'name' | 'email' | 'phone';
  value: string;
}

export async function loadPeople(
  store: PeopleStore,
  loadParams: APILoadParams,
  params?: UncachedLoadPeopleParams,
): Promise<LoadPeopleResponse['items']>;

export async function loadPeople(
  store: PeopleStore,
  loadParams: APILoadParams,
  params: CachedLoadPeopleParams,
): Promise<void>;

export async function loadPeople(
  { state, dispatch }: PeopleStore,
  { force, api, auth }: APILoadParams,
  params?: LoadPeopleParams,
): Promise<void | LoadPeopleResponse['items']> {
  const cache = params?.cache === false ? false : true;
  if (!shouldLoad(state.loaded, force, auth)) {
    return;
  }

  const res = await apiInvoke<LoadPeopleResponse>(
    api,
    Paths.people,
    auth,
    makePayloadRequestInit(params || {}),
  );
  if (!cache) {
    // eslint-disable-next-line consistent-return
    return res.items;
  }

  dispatch({
    type: 'setPeople',
    data: res.items,
    count: res.count,
    merge: Boolean(params?.offset),
  });
}

export async function loadPerson(
  { computed, dispatch }: PeopleStore,
  {
    force,
    api,
    auth,
    id,
    cache = false,
  }: APILoadParams & { id: string; cache: boolean },
) {
  if (!force && computed.peopleLookup[id]) {
    return { loaded: false, exists: true };
  }

  const path = `${Paths.people}/${id}`;
  const res = await apiInvoke<LoadPersonResponse>(api, path, auth);
  if (!res.exists) {
    return { exists: false, loaded: false };
  }

  const person = { id: res.id, data: res.data } as Person;
  if (cache) {
    dispatch({ type: 'addPerson', item: person });
  }

  return {
    loaded: true,
    exists: true,
    item: person,
  };
}

export async function updatePerson(
  {
    store,
    onUpdated,
  }: { store?: PeopleStore; onUpdated?: (item: Person) => void },
  { api, auth, item }: APIParams & { item: Person },
) {
  const path = `${Paths.people}/${item.id}`;
  const res = await apiInvoke<UpdatePersonResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(item.data, 'PATCH'),
  );
  const { updated, ...updatedItem } = res;
  if (updated) {
    if (store) {
      store.dispatch({
        type: 'updatePerson',
        item: updatedItem satisfies Person,
      });
    }

    if (onUpdated) {
      onUpdated(updatedItem satisfies Person);
    }
  }
}

export type LoadHouseholdResponse = HouseholdAndMemberData | HouseholdNotFound;
export type HouseholdAndMemberData = {
  exists: true;
  household: Household;
  people: Record<PersonId, Person>;
};
export type HouseholdNotFound = { exists: false };

export async function getHousehold({
  id,
  api,
  auth,
}: {
  id: string;
} & APIParams) {
  const path = `${Paths.households}/${id}`;
  const res = await apiInvoke<LoadHouseholdResponse>(api, path, auth);

  return res;
}
